import { useState, useCallback } from 'react';
import { MapPresenter } from '../MapPresenter';
import { MapWidgetPosition } from '../components/widgets/MapWidget.types';
import { MapSettingsService } from '../services/MapSettingsService';
import { ActiveLayersOpacity, ActiveLayersReordering, UserI } from '../../../components/sitebase/Base.types';
import IS_EFAS from '../../../components/sitebase/Environment';

export interface MapSettingsReturn {
    selectedBaseMap: number;
    baseMapOpacity: number;
    useMapMarkers: boolean;
    rememberMapMarkers: boolean;
    closeRemovesPoint: boolean;
    forceMapserver: boolean;
    ignoreLatestForecastTime: boolean;
    forecastWidgetPosition: MapWidgetPosition;
    activeLayersOpacity: ActiveLayersOpacity;
    activeLayersReordering: ActiveLayersReordering;
    setBaseMap: (baseMapId: number) => void;
    setBaseMapOpacity: (opacity: number) => void;
    setUseMapMarkers: (useMarkers: boolean) => void;
    setRememberMapMarkers: (val: boolean) => void;
    setCloseRemovesPoint: (val: boolean) => void;
    setForceMapserver: (val: boolean) => void;
    setIgnoreLatestForecastTime: (val: boolean) => void;
    setForecastWidgetPosition: (position: MapWidgetPosition) => void;
    setActiveLayersOpacity: (mode: ActiveLayersOpacity) => void;
    setActiveLayersReordering: (mode: ActiveLayersReordering) => void;
    resetWidgetSizes: () => void;
}

export function useMapSettings(
    presenter: MapPresenter,
    remote: MapSettingsService,
    webSettings: UserI['web_settings']
): MapSettingsReturn {
    const [selectedBaseMap, setSelectedBaseMapState] = useState<number>(
        parseInt(webSettings.base_map) || 1
    );
    const [baseMapOpacity, setBaseMapOpacityState] = useState<number>(1);
    const [useMapMarkers, setUseMapMarkersState] = useState<boolean>(
        webSettings.use_map_markers
    );
    const [rememberMapMarkers, setRememberMapMarkersState] = useState<boolean>(
        webSettings.remember_map_markers
    );
    const [closeRemovesPoint, setCloseRemovesPointState] = useState<boolean>(
        webSettings.close_removes_point ?? !IS_EFAS
    );
    const [forceMapserver, setForceMapserverState] = useState<boolean>(false);
    const [ignoreLatestForecastTime, setIgnoreLatestForecastTimeState] = useState<boolean>(false);
    const [forecastWidgetPosition, setForecastWidgetPositionState] = useState<MapWidgetPosition>(
        webSettings.forecast_date_position || MapWidgetPosition.RIGHT
    );
    const [activeLayersOpacity, setActiveLayersOpacityState] = useState<ActiveLayersOpacity>(
        (webSettings.active_layers_opacity as ActiveLayersOpacity) || ActiveLayersOpacity.ALWAYS_VISIBLE
    );
    const [activeLayersReordering, setActiveLayersReorderingState] = useState<ActiveLayersReordering>(
        (webSettings.active_layers_reordering as ActiveLayersReordering) || ActiveLayersReordering.DRAGGING
    );
    const setBaseMap = useCallback((baseMapId: number) => {
        console.debug("Setting base map", baseMapId);
        presenter.setBaseMap(baseMapId);
        remote.updateUserWebSettings({ base_map: baseMapId.toString() });
        setSelectedBaseMapState(baseMapId);
    }, [presenter, remote]);

    const setBaseMapOpacity = useCallback((opacity: number) => {
        console.debug("Setting base map opacity", opacity);
        setBaseMapOpacityState(opacity);
        presenter.setBaseMapOpacity(opacity);
    }, [presenter]);

    const setUseMapMarkers = useCallback((useMarkers: boolean) => {
        console.debug("Setting use map markers to", useMarkers);
        setUseMapMarkersState(useMarkers);
        remote.updateUserWebSettings({ use_map_markers: useMarkers });
    }, [remote]);

    const setRememberMapMarkers = useCallback((val: boolean) => {
        console.debug("Setting remember map markers to", val);
        setRememberMapMarkersState(val);
        remote.updateUserWebSettings({ remember_map_markers: val });
    }, [remote]);

    const setCloseRemovesPoint = useCallback((val: boolean) => {
        console.debug("Setting close removes point to", val);
        setCloseRemovesPointState(val);
        remote.updateUserWebSettings({ close_removes_point: val });
    }, [remote]);

    const setForceMapserver = useCallback((val: boolean) => {
        console.debug("Setting force mapserver to", val);
        setForceMapserverState(val);
    }, []);

    const setIgnoreLatestForecastTime = useCallback((val: boolean) => {
        console.debug("Setting ignore latest forecast time to", val);
        setIgnoreLatestForecastTimeState(val);
    }, []);

    const setForecastWidgetPosition = useCallback((position: MapWidgetPosition) => {
        console.debug("Forecast widget position change", position);
        setForecastWidgetPositionState(position);
        remote.updateUserWebSettings({ forecast_date_position: position });
    }, [remote]);

    const setActiveLayersOpacity = useCallback((mode: ActiveLayersOpacity) => {
        console.debug("Setting active layers opacity mode to", mode);
        setActiveLayersOpacityState(mode);
        remote.updateUserWebSettings({ activeLayersOpacity: mode });
    }, [remote]);

    const setActiveLayersReordering = useCallback((mode: ActiveLayersReordering) => {
        console.debug("Setting active layers reordering mode to", mode);
        setActiveLayersReorderingState(mode);
        remote.updateUserWebSettings({ activeLayersReordering: mode });
    }, [remote]);

    const resetWidgetSizes = useCallback(() => {
        console.debug("Resetting window sizes and positions");
        if (confirm("Are you sure you want to reset all window sizes and positions to default values?"
            + " This can help if a window is stuck off-screen. This requires a page reload.")) {
            remote.updateUserWebSettings({
                'feature_info_window_dimensions': {},
                'legend_window_dimensions': {}
            });
            location.reload();
        }
    }, [remote]);

    return {
        selectedBaseMap,
        baseMapOpacity,
        useMapMarkers,
        rememberMapMarkers,
        closeRemovesPoint,
        forceMapserver,
        ignoreLatestForecastTime,
        forecastWidgetPosition,
        activeLayersOpacity,
        activeLayersReordering,
        setBaseMap,
        setBaseMapOpacity,
        setUseMapMarkers,
        setRememberMapMarkers,
        setCloseRemovesPoint,
        setForceMapserver,
        setIgnoreLatestForecastTime,
        setForecastWidgetPosition,
        setActiveLayersOpacity,
        setActiveLayersReordering,
        resetWidgetSizes,
    };
}
